/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LmsShell, lmsApi, certsApi, paymentsApi, studentApi, getStudentToken, clearStudentSession } from "./portal/shell";
import { PracticeBanner } from "./portal/PracticeBanner";
import { toast, Toaster } from "sonner";
import {
  LogOut, Timer, ChevronRight, Award, Sparkles, Download, FileCheck2, FileText, Receipt,
  Flame, GraduationCap, Trophy, Target, Star, PlayCircle, Lock, School, CalendarDays,
  FileDown,
} from "lucide-react";

/* Deterministic daily quote — same quote for all students on a given day,
   so classroom teachers at bulk schools see the same motivation their
   students see when they open their dashboards. */
const DAILY_QUOTES = [
  "A journey of a thousand miles begins with a single step. — Lao Tzu",
  "The expert in anything was once a beginner.",
  "Learning is a treasure that will follow its owner everywhere. — Chinese proverb",
  "Discipline is the bridge between goals and accomplishment. — Jim Rohn",
  "Small daily improvements over time lead to stunning results.",
  "You don't have to be great to start, but you have to start to be great. — Zig Ziglar",
  "The sky above us is the limit — and even that's negotiable.",
  "Study hard what interests you the most, in the most undisciplined way possible. — Richard Feynman",
  "Every expert was once a beginner who refused to quit.",
  "Somewhere, something incredible is waiting to be known. — Carl Sagan",
];
function dailyQuote() {
  const d = new Date();
  const seed = d.getUTCFullYear() * 1000 + (d.getUTCMonth() + 1) * 32 + d.getUTCDate();
  return DAILY_QUOTES[seed % DAILY_QUOTES.length];
}

const MILESTONE_ICONS = {
  first_lesson: PlayCircle,
  halfway: Target,
  course_complete: GraduationCap,
  exam_passed: Trophy,
  certificate_earned: Star,
};

export default function StudentDashboard() {
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [certs, setCerts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!getStudentToken()) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const [prog, sum] = await Promise.all([
          lmsApi.progress(),
          studentApi.dashboardSummary(),
        ]);
        const p0 = (prog && prog[0]) || null;
        setProgress(p0);
        setSummary(sum);
        const cid = (p0 && p0.course_id) || "advanced-rpas-2026";
        const c = await lmsApi.courseFull(cid);
        setCourse(c);
        try {
          const mine = await certsApi.mine();
          setCerts(mine || []);
        } catch { /* certs list is non-critical */ }
        try {
          const r = await paymentsApi.receipts();
          setReceipts((r && r.receipts) || []);
        } catch { /* receipts list is non-critical */ }
      } catch (e) {
        if (e.status === 401) { clearStudentSession(); nav("/login", { replace: true }); return; }
        setErr(e.message || "Load failed");
      }
    })();
  }, [nav]);

  const signOut = () => { clearStudentSession(); nav("/login", { replace: true }); };
  const rightSlot = (
    <button onClick={signOut} className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="student-signout">
      <LogOut size={12} /> Sign out
    </button>
  );

  const modulesSorted = useMemo(() => {
    if (!course) return [];
    return course.modules.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [course]);

  const quote = useMemo(() => dailyQuote(), []);

  return (
    <LmsShell subtitle="Dashboard" rightSlot={rightSlot} wide>
      <Toaster theme="dark" position="top-right" />
      <div className="tbg-lms-wide" data-testid="student-dashboard">
        <PracticeBanner />

        {/* School banner for bulk students */}
        {summary?.school_organization_name && (
          <div className="tbg-school-banner" data-testid="school-banner">
            <School size={16} color="#f0be3c" aria-hidden="true" />
            <div>
              <div className="lbl">You're learning as part of </div>
              <div className="org">{summary.school_organization_name}</div>
            </div>
          </div>
        )}

        {/* Personalized welcome header */}
        {summary && (
          <div className="tbg-welcome" data-testid="welcome-card">
            <div>
              <h1 data-testid="welcome-greeting">
                Welcome back, {summary.first_name}
              </h1>
              <div className="tbg-welcome-meta">
                <span>{summary.joined_label}</span>
                <span>·</span>
                <span>{summary.lessons_completed}/{summary.lessons_total} lessons</span>
              </div>
              <div className="tbg-welcome-quote" data-testid="daily-quote">
                "{quote}"
              </div>
            </div>
            <ProgressRing pct={summary.progress_pct} />
          </div>
        )}

        {course?.preview_mode && <PreviewModeCard />}

        {/* Stats strip */}
        {summary && (
          <div className="tbg-stats" data-testid="stats-strip">
            <div className={`tbg-stat ${summary.streak_days > 0 ? "hot" : ""}`} data-testid="stat-streak">
              <div className="lbl">
                <Flame size={10} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                Day Streak
              </div>
              <div className="val">
                {summary.streak_days}
                <small>{summary.streak_best > summary.streak_days ? `best ${summary.streak_best}` : ""}</small>
              </div>
            </div>
            <div className="tbg-stat" data-testid="stat-active-days">
              <div className="lbl">Active (30d)</div>
              <div className="val">{summary.active_days_30}<small>days</small></div>
            </div>
            <div className="tbg-stat" data-testid="stat-progress">
              <div className="lbl">Progress</div>
              <div className="val">{summary.progress_pct}<small>%</small></div>
            </div>
          </div>
        )}

        {/* Next up */}
        {summary?.next_up && (
          <Link
            to={`/dashboard/modules/${summary.next_up.module_slug}`}
            className="tbg-nextup"
            style={{ textDecoration: "none" }}
            data-testid="next-up-card"
          >
            <div>
              <div className="lbl">Next up</div>
              <div className="ttl">{summary.next_up.lesson_title}</div>
              <div className="sub">
                {summary.next_up.module_code} · {summary.next_up.module_title}
                {" · "}Lesson {summary.next_up.lesson_index}/{summary.next_up.total_in_module}
              </div>
            </div>
            <span className="tbg-btn" style={{ pointerEvents: "none" }}>
              Resume <ChevronRight size={14} />
            </span>
          </Link>
        )}

        {/* Learning goal */}
        {summary && (
          <GoalCard
            summary={summary}
            onChange={async (date) => {
              try {
                const fresh = await studentApi.setGoal(date);
                setSummary(fresh);
                toast.success(date ? "Goal saved" : "Goal cleared");
              } catch (e) {
                toast.error(e.message || "Could not save goal");
              }
            }}
          />
        )}

        {/* Milestones */}
        {summary && summary.milestones?.length > 0 && (
          <>
            <div className="tbg-section-sub">Milestones</div>
            <div className="tbg-milestones" data-testid="milestones">
              {summary.milestones.map((m) => {
                const Icon = MILESTONE_ICONS[m.key] || Star;
                return (
                  <div
                    key={m.key}
                    className={`tbg-milestone ${m.earned ? "earned" : "locked"}`}
                    data-testid={`milestone-${m.key}`}
                  >
                    <div className="icon">
                      {m.earned ? <Icon size={18} /> : <Lock size={14} />}
                    </div>
                    <div className="ttl">{m.title}</div>
                    <div className="desc">{m.description}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="tbg-panel" style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="tbg-section-sub">Advanced RPAS · Enrolled</div>
              <h1 className="tbg-section-title">{course?.title || "Advanced RPAS Ground School"}</h1>
              <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>
                Work through the 8 core modules, take the module quizzes for feedback, then
                attempt the full-length practice exam when you're ready. Bonus module for
                your Flight Review is at the end.
              </p>
            </div>
            <ProgressReportButton />
          </div>
        </div>

        <div className="tbg-section-sub">Modules</div>
        <div className="tbg-modlist" style={{ marginBottom: 24 }}>
          {modulesSorted.map((m) => (
            <ModuleCard
              key={m.id} m={m}
              completedLessons={new Set(progress?.completed_lesson_ids || [])}
              quizScore={progress?.quiz_scores?.[m.id]}
            />
          ))}
        </div>

        <div className="tbg-section-sub">Practice Exam</div>
        <ExamCard progress={progress} course={course} />

        {certs.length > 0 && (
          <>
            <div className="tbg-section-sub" style={{ marginTop: 22 }}>Your Certificates</div>
            {certs.map((c) => <CertificateCard key={c.id} cert={c} />)}
          </>
        )}

        {receipts.length > 0 && (
          <>
            <div className="tbg-section-sub" style={{ marginTop: 22 }}>Tax Invoices</div>
            {receipts.map((r) => <ReceiptCard key={r.session_id} receipt={r} />)}
          </>
        )}

        {err && <div className="tbg-lms-err" style={{ marginTop: 12 }}>{err}</div>}

        <div className="tbg-lms-foot" style={{ marginTop: 30 }}>
          <Link to="/groundschool">Course overview</Link>
          <Link to="/verify">Certificate verifier</Link>
        </div>
      </div>
    </LmsShell>
  );
}

/* ---- Learning goal card ---- */
function GoalCard({ summary, onChange }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(summary.goal_target_date || "");
  const [busy, setBusy] = useState(false);
  const hasGoal = !!summary.goal_target_date;
  const status = summary.goal_status || "none";

  const save = async () => {
    if (!date) return;
    setBusy(true);
    await onChange(date);
    setBusy(false);
    setEditing(false);
  };
  const clear = async () => {
    setBusy(true);
    await onChange(null);
    setBusy(false);
    setDate("");
    setEditing(false);
  };

  const today = new Date().toISOString().slice(0, 10);

  if (!hasGoal && !editing) {
    return (
      <div className="tbg-goal" data-testid="goal-card-empty">
        <div className="top">
          <div>
            <div className="lbl"><Target size={10} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />Learning goal</div>
            <div className="countdown" style={{ fontSize: 16, fontWeight: 600 }}>Set a target date to stay on track</div>
            <div className="msg">
              Pick a date you want to finish the course by — I'll show you a gentle pace check each visit.
            </div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="tbg-btn"
            data-testid="goal-set-btn"
          >
            <CalendarDays size={13} /> Set goal
          </button>
        </div>
      </div>
    );
  }

  const niceDate = hasGoal
    ? new Date(summary.goal_target_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className={`tbg-goal ${status}`} data-testid="goal-card">
      <div className="top">
        <div>
          <div className="lbl">
            <Target size={10} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
            {status === "achieved" ? "Goal achieved" : "Your learning goal"}
          </div>
          {hasGoal && (
            <div className="countdown" data-testid="goal-countdown">
              {summary.goal_days_remaining >= 0
                ? `${summary.goal_days_remaining} day${summary.goal_days_remaining === 1 ? "" : "s"}`
                : `${Math.abs(summary.goal_days_remaining)} day${Math.abs(summary.goal_days_remaining) === 1 ? "" : "s"} overdue`}
              <small>target {niceDate}</small>
            </div>
          )}
          {summary.goal_pace_msg && (
            <div className="msg" data-testid="goal-msg">{summary.goal_pace_msg}</div>
          )}
        </div>
      </div>
      <div className="controls">
        {!editing ? (
          <>
            <button onClick={() => setEditing(true)} className="tbg-btn ghost" data-testid="goal-edit-btn">
              <CalendarDays size={12} /> Change date
            </button>
            <button onClick={clear} disabled={busy} className="tbg-btn ghost" data-testid="goal-clear-btn">
              Clear goal
            </button>
          </>
        ) : (
          <>
            <input
              type="date"
              value={date}
              min={today}
              onChange={(e) => setDate(e.target.value)}
              data-testid="goal-date-input"
            />
            <button onClick={save} disabled={busy || !date} className="tbg-btn" data-testid="goal-save-btn">
              {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setDate(summary.goal_target_date || ""); }} className="tbg-btn ghost">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Progress Report download button ---- */
function ProgressReportButton() {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      await studentApi.downloadProgressReport();
      toast.success("Progress report downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      className="tbg-btn ghost"
      style={{ alignSelf: "flex-start" }}
      data-testid="progress-report-btn"
    >
      <FileDown size={13} /> {busy ? "Preparing…" : "Progress report (PDF)"}
    </button>
  );
}


/* Progress ring — SVG, no external deps. */
function ProgressRing({ pct = 0 }) {
  const safe = Math.max(0, Math.min(100, pct || 0));
  const r = 38;
  const c = 2 * Math.PI * r;
  const offset = c - (safe / 100) * c;
  return (
    <div className="tbg-progress-ring" data-testid="progress-ring" aria-label={`${safe}% complete`}>
      <svg width="92" height="92" viewBox="0 0 92 92">
        <circle className="track" cx="46" cy="46" r={r} strokeWidth="7" fill="none" />
        <circle
          className="fill" cx="46" cy="46" r={r} strokeWidth="7" fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
        />
      </svg>
      <div className="pct">
        {safe}%
        <small>DONE</small>
      </div>
    </div>
  );
}

function CertificateCard({ cert }) {
  const [busy, setBusy] = useState(false);
  const issued = cert.issued_at ? new Date(cert.issued_at) : null;

  const onDownload = async () => {
    setBusy(true);
    try {
      await certsApi.download(cert.number);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="tbg-panel"
      style={{
        marginTop: 8,
        borderColor: cert.revoked ? "rgba(244,113,113,0.5)" : "rgba(61,192,175,0.5)",
        background: cert.revoked
          ? "linear-gradient(180deg, rgba(244,113,113,0.06), #08110f)"
          : "linear-gradient(180deg, rgba(61,192,175,0.06), #08110f)",
      }}
      data-testid={`cert-card-${cert.number}`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1, minWidth: 260 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 4,
            background: cert.revoked ? "rgba(244,113,113,0.12)" : "rgba(61,192,175,0.12)",
            border: `1px solid ${cert.revoked ? "rgba(244,113,113,0.5)" : "rgba(61,192,175,0.5)"}`,
            display: "grid", placeItems: "center", flexShrink: 0,
          }}>
            <FileCheck2 size={16} color={cert.revoked ? "#f47171" : "#3dc0af"} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="tbg-section-sub" style={{ marginBottom: 3 }}>
              {cert.revoked ? "Certificate revoked" : "Certificate of Completion"}
            </div>
            <h2 className="tbg-section-title" style={{ fontSize: 16, margin: 0 }}>
              {cert.course_name}
            </h2>
            <div style={{ marginTop: 8, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.08em" }}>
              <span style={{ color: cert.revoked ? "#f47171" : "#3dc0af" }}>{cert.number}</span>
              {issued && <> · Issued {issued.toISOString().slice(0, 10)}</>}
              {cert.score_pct != null && <> · {cert.score_pct.toFixed(1)}%</>}
            </div>
            {cert.revoked && cert.revoked_reason && (
              <p style={{ color: "#f47171", fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
                Reason: {cert.revoked_reason}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={onDownload}
            disabled={busy}
            className="tbg-btn"
            data-testid={`cert-download-${cert.number}`}
          >
            <Download size={13} /> {busy ? "Preparing…" : "Download PDF"}
          </button>
          <Link
            to={`/verify?n=${encodeURIComponent(cert.number)}`}
            className="tbg-btn ghost"
            data-testid={`cert-verify-${cert.number}`}
          >
            Verify
          </Link>
        </div>
      </div>
    </div>
  );
}

function PreviewModeCard() {
  return (
    <div
      className="tbg-panel"
      data-testid="course-preview-card"
      style={{
        marginBottom: 16,
        borderColor: "rgba(61,192,175,0.5)",
        background: "linear-gradient(180deg, rgba(61,192,175,0.06), #08110f)",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{
          width: 34, height: 34, borderRadius: 4,
          background: "rgba(61,192,175,0.12)", border: "1px solid rgba(61,192,175,0.5)",
          display: "grid", placeItems: "center", flexShrink: 0,
        }}>
          <Sparkles size={16} color="#3dc0af" />
        </div>
        <div style={{ flex: 1 }}>
          <div className="tbg-section-sub" style={{ marginBottom: 4 }}>Course Preview Mode</div>
          <h2 className="tbg-section-title" style={{ fontSize: 16, margin: 0 }}>
            Real curriculum coming soon
          </h2>
          <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>
            You're one of the first students in — thank you. Our licensed
            Transport Canada curriculum is being finalized right now. In the meantime,
            you can <b>preview the exam engine</b> below: the quiz and 60-minute
            timed exam flow are fully functional, with clearly-labelled
            <span className="tbg-placeholder-tag" style={{ margin: "0 4px" }}>SAMPLE</span>
            questions so you can see how it works.
          </p>
          <p style={{ color: "#7b8b87", fontSize: 12, marginTop: 8, lineHeight: 1.55 }}>
            You'll get an email the moment real content lands. Your account, progress,
            and course access stay locked in.
          </p>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ m, completedLessons, quizScore }) {
  const totalLessons = (m.lessons || []).length;
  const doneLessons = (m.lessons || []).filter((l) => completedLessons.has(l.id)).length;
  const pct = totalLessons === 0 ? 0 : Math.round((doneLessons / totalLessons) * 100);
  return (
    <Link to={`/dashboard/modules/${m.slug}`} className="tbg-modcard" data-testid={`module-card-${m.slug}`}>
      <div className="code">{m.code}</div>
      <div className="body">
        <div className="title">
          {m.title}
          {m.is_bonus && <span className="bonus-badge">Bonus</span>}
        </div>
        <div className="meta">
          {m.est_hours ? `${m.est_hours}h` : "—"}
          {totalLessons > 0 && ` · ${doneLessons}/${totalLessons} lessons`}
          {totalLessons === 0 && !m.is_bonus && " · Content pending"}
          {quizScore && ` · Best quiz ${quizScore.best_pct}%`}
        </div>
        {totalLessons > 0 && (
          <div className="bar"><span style={{ width: `${pct}%` }} /></div>
        )}
      </div>
      <ChevronRight size={18} color="#3dc0af" style={{ alignSelf: "center" }} />
    </Link>
  );
}

function ExamCard({ progress, course }) {
  const canStart = progress ? progress.exam_can_start : true;
  const attempts = progress?.exam_attempts_count || 0;
  const best = progress?.exam_best_pct;
  const nextAt = progress?.exam_next_allowed_at ? new Date(progress.exam_next_allowed_at) : null;
  const passed = best !== null && best !== undefined && best >= (course?.exam?.pass_pct || 80);

  return (
    <div className="tbg-panel" style={{ marginTop: 8 }} data-testid="exam-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 className="tbg-section-title">Full-length Practice Exam</h2>
          <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 6, lineHeight: 1.55, maxWidth: 560 }}>
            50 questions · 60 minutes · 80% pass · 90-minute cooldown between attempts.
            Mirrors the real Transport Canada Small Advanced Exam format.
          </p>
          <div style={{ marginTop: 12, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.08em" }}>
            Attempts: {attempts} · Best: {best !== null && best !== undefined ? `${best}%` : "—"}
            {passed && (
              <span style={{ marginLeft: 10, color: "#3dc0af", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Award size={11} /> Practice pass
              </span>
            )}
          </div>
          {!canStart && nextAt && (
            <div style={{ marginTop: 8, font: "500 11px 'JetBrains Mono', monospace", color: "#f0be3c" }}>
              Next attempt available: {nextAt.toLocaleString()}
            </div>
          )}
        </div>
        <Link
          to="/dashboard/exam"
          className={`tbg-btn${canStart ? "" : " ghost"}`}
          data-testid="launch-exam"
        >
          <Timer size={13} /> {canStart ? "Launch practice exam" : "View cooldown"}
        </Link>
      </div>
    </div>
  );
}


function ReceiptCard({ receipt }) {
  const [busy, setBusy] = useState(false);
  const issued = receipt.invoice_issued_at ? new Date(receipt.invoice_issued_at) : null;
  const amount = receipt.amount_total_cents != null
    ? `$${(receipt.amount_total_cents / 100).toFixed(2)}`
    : `$${(receipt.amount || 0).toFixed(2)}`;
  const download = async () => {
    setBusy(true);
    try {
      await paymentsApi.downloadReceipt(receipt.session_id, receipt.invoice_number);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="tbg-panel"
      style={{
        marginTop: 8,
        borderColor: "rgba(201,168,255,0.4)",
        background: "linear-gradient(180deg, rgba(201,168,255,0.05), #08110f)",
      }}
      data-testid={`receipt-card-${receipt.session_id}`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1, minWidth: 260 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 4,
            background: "rgba(201,168,255,0.12)",
            border: "1px solid rgba(201,168,255,0.4)",
            display: "grid", placeItems: "center", flexShrink: 0,
          }}>
            <Receipt size={16} color="#c9a8ff" />
          </div>
          <div style={{ flex: 1 }}>
            <div className="tbg-section-sub" style={{ marginBottom: 3, color: "#c9a8ff" }}>
              Tax Invoice
            </div>
            <h2 className="tbg-section-title" style={{ fontSize: 16, margin: 0 }}>
              {receipt.invoice_number || "Unnumbered receipt"}
            </h2>
            <div style={{ marginTop: 8, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.08em" }}>
              <span style={{ color: "#e2ece9" }}>{amount} {receipt.currency}</span>
              {issued && <> · Issued {issued.toISOString().slice(0, 10)}</>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={download}
            disabled={busy}
            className="tbg-btn"
            data-testid={`download-receipt-${receipt.session_id}`}
            style={{ borderColor: "rgba(201,168,255,0.6)", color: "#c9a8ff" }}
          >
            <FileText size={13} /> {busy ? "Downloading…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
